import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import { Alert, Box, Stack, CircularProgress, Typography } from '@mui/material';

import { Space, SpaceType } from '../../mainApi';
import { getVisibleSpaces, useMainStore } from '../../stores/mainStore';
import { useSpacesStore } from '../../stores/spacesStore';
import { SpaceCard } from './SpaceCard';

type SpacesGridProps = {
  onSpaceMenuOpen: (event: React.MouseEvent<HTMLElement>, space: Space) => void;
  onCreateSpace: (spaceType: SpaceType) => void;
};

type AddSpaceCardProps = {
  color: string;
  label: string;
  compact?: boolean;
  onClick: () => void;
};

const AddSpaceCard = ({ color, label, compact = false, onClick }: AddSpaceCardProps) => (
  <Box
    onClick={onClick}
    sx={{
      height: compact ? 55 : 99,
      borderRadius: 1,
      bgcolor: color,
      color: '#ffffff',
      display: 'flex',
      flexDirection: compact ? 'row' : 'column',
      justifyContent: 'center',
      alignItems: 'center',
      gap: compact ? 1 : 0.75,
      px: compact ? 2 : 0,
      fontWeight: 700,
      cursor: 'pointer',
      userSelect: 'none',
      border: '1px solid transparent',
    }}
  >
    <AddCircleOutlineIcon sx={{ fontSize: compact ? 22 : 30, flexShrink: 0 }} />
    <Typography sx={{ fontWeight: 700, fontSize: compact ? 14 : 15, whiteSpace: 'nowrap' }}>
      {label}
    </Typography>
  </Box>
);

export const SpacesGrid = ({ onSpaceMenuOpen, onCreateSpace }: SpacesGridProps) => {
  const { orderBy, filter, selectedWorkspaceId } = useMainStore();
  const { spaces, isLoadingSpaces, spacesError } = useSpacesStore();

  const visibleSpaces = getVisibleSpaces(spaces, filter, selectedWorkspaceId, orderBy);

  if (isLoadingSpaces && spaces.length === 0) {
    return (
      <Stack alignItems="center" sx={{ py: 10 }}>
        <CircularProgress />
      </Stack>
    );
  }

  if (spacesError && spaces.length === 0) {
    return <Alert severity="error">{spacesError}</Alert>;
  }

  return (
    <>
      {spacesError ? (
        <Alert severity="error" sx={{ mb: 3 }}>
          {spacesError}
        </Alert>
      ) : null}
      <Box
        sx={
          visibleSpaces.length > 0
            ? {
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
                gap: 3,
                alignItems: 'start',
              }
            : {
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'flex-start',
                gap: 3,
              }
        }
      >
        <Stack spacing={1.5} minWidth={253}>
          <AddSpaceCard
            color="#c13d6c"
            label="New board"
            compact
            onClick={() => onCreateSpace('board')}
          />
          <AddSpaceCard
            color="#7dc9fb"
            label="New whiteboard"
            compact
            onClick={() => onCreateSpace('wall')}
          />
          <AddSpaceCard
            color="#ffd04b"
            label="New Quick Sort"
            compact
            onClick={() => onCreateSpace('game')}
          />
        </Stack>
        {visibleSpaces.length === 0 ? (
          <Box
            sx={{
              p: 4,
              textAlign: 'center',
              bgcolor: '#fff',
              border: '1px solid #e6e8ef',
              borderRadius: 1,
              minHeight: 99,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexGrow: 1,
            }}
          >
            <Typography color="text.secondary">No spaces found.</Typography>
          </Box>
        ) : null}
        {visibleSpaces.map((space) => (
          <SpaceCard
            key={`${space.type}-${space.id ?? space._id}`}
            space={space}
            onSpaceMenuOpen={onSpaceMenuOpen}
          />
        ))}
      </Box>
    </>
  );
};
